/** Height selection for planning drag overlay previews. */
import {
  DEFAULT_TASK_DURATION_MINS,
  FIFTEEN_MINUTE_SLOT_HEIGHT,
  TIME_SLOT_MINS,
} from '@/config/constants';

/**
 * Pixel height of a scheduled-task preview on the day time-grid.
 * Mirrors the duration↔height ratio used by useTaskResize /
 * calculateTaskPositions (without the 1px border inset).
 *
 * @param {number} [durationMins]
 * @param {number} [fifteenMinuteSlotHeight]
 * @returns {number}
 */
export const computeTimeGridOverlayHeight = (
  durationMins = DEFAULT_TASK_DURATION_MINS,
  fifteenMinuteSlotHeight = FIFTEEN_MINUTE_SLOT_HEIGHT,
) => {
  const duration =
    typeof durationMins === 'number' && durationMins > 0
      ? durationMins
      : DEFAULT_TASK_DURATION_MINS;
  const slotHeight =
    typeof fifteenMinuteSlotHeight === 'number' && fifteenMinuteSlotHeight > 0
      ? fifteenMinuteSlotHeight
      : FIFTEEN_MINUTE_SLOT_HEIGHT;
  return (duration / TIME_SLOT_MINS) * slotHeight;
};

/**
 * Choose overlay height: duration-based when hovering the day time-grid,
 * otherwise keep the source-element height captured at drag-start.
 *
 * @param {Object} params
 * @param {'day'|'week'|'month'|null|undefined} [params.gridView]
 * @param {number} [params.durationMins]
 * @param {number} [params.sourceHeight]
 * @param {number} [params.fifteenMinuteSlotHeight]
 * @returns {number}
 */
export const resolveDragOverlayHeight = ({
  gridView,
  durationMins,
  sourceHeight,
  fifteenMinuteSlotHeight = FIFTEEN_MINUTE_SLOT_HEIGHT,
} = {}) => {
  if (gridView === 'day') {
    return computeTimeGridOverlayHeight(durationMins, fifteenMinuteSlotHeight);
  }
  return (
    (typeof sourceHeight === 'number' && sourceHeight > 0
      ? sourceHeight
      : null) ?? FALLBACK_SOURCE_HEIGHT_PX
  );
};

const FALLBACK_SOURCE_HEIGHT_PX = 30;
